import React, { useState, useEffect } from "react";
import {
  Clock,
  Upload,
  RefreshCw,
  FileText,
  Users,
  Layers,
  AlertTriangle,
  Loader,
  BarChart,
  Crown,
  CreditCard,
  XCircle,
  CheckCircle,
  AlertCircle,
  HelpCircle,
} from "lucide-react";
import UsageProgressBar from "./UsageProgressBar";
import {
  fetchUserSubscription,
  formatSubscriptionPrice,
  cancelSubscription,
} from "./subscriptionUtils";
import SubscriptionModal from "./SubscriptionModal";
import SubscriptionFAQs from "./SubscriptionFAQs";
import { apiCall } from '../../utils/api';

const SubscriptionDashboard = ({ user, appId = "saleswit" }) => {
  const [subscription, setSubscription] = useState(null);
  const [usageStats, setUsageStats] = useState({
    documents: 0,
    battlecards: 0,
    queries: 0,
    users: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showFAQs, setShowFAQs] = useState(false); // New state for FAQs modal
  
  // New state to track if a recently created Razorpay subscription exists
  const [hasRecentRazorpaySubscription, setHasRecentRazorpaySubscription] = useState(false);
  
  // Check for URL parameters indicating a recent subscription creation
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const fromPayment = queryParams.get('from_payment');
    const gateway = queryParams.get('gateway');
    
    if (fromPayment === 'true' && gateway === 'razorpay') {
      setHasRecentRazorpaySubscription(true);
      
      // Clear URL parameters without refreshing page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  useEffect(() => {
    // Check for upgrade status in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const upgradeStatus = urlParams.get('upgrade');
    const message = urlParams.get('message');
    
    if (upgradeStatus && message) {
      if (upgradeStatus === 'success') {
        setSuccessMessage(decodeURIComponent(message));
      } else if (upgradeStatus === 'error') {
        setError(decodeURIComponent(message));
      }
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);


  useEffect(() => {
    if (user) {
      fetchSubscriptionData();
      fetchUsageStats();
      fetchBillingHistory();
    }
  }, [user, appId]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const subscriptionData = await fetchUserSubscription(user.uid, appId);
      setSubscription(subscriptionData);
      
      // Check if subscription is newly created and not yet active
      if (subscriptionData && 
          subscriptionData.razorpay_subscription_id && 
          subscriptionData.status !== 'active') {
        setHasRecentRazorpaySubscription(true);
      } else if (subscriptionData && 
                subscriptionData.status === 'active') {
        // Clear the notification if subscription is active
        setHasRecentRazorpaySubscription(false);
      }
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setError("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      // Fetch the resource quota (remaining resources)
      const quotaResponse = await apiCall(
        `/api/subscriptions/resource-quota?user_id=${user.uid}&app_id=${appId}`
      );

      if (!quotaResponse.ok) {
        throw new Error("Failed to fetch resource quota");
      }
      
      const quotaData = await quotaResponse.json();
      console.log('Quota API Response:', quotaData);
      console.log('Quota data structure:', JSON.stringify(quotaData, null, 2));
      console.log('Requests quota:', quotaData.quota?.requests);
      
      // Set the usage stats with the quota information
      setUsageStats({
        // Other stats may be here
        requests: quotaData.quota.requests || 0});
    } catch (err) {
      console.error("Error fetching usage stats:", err);
      // Don't set error state, just continue with default values
      setUsageStats({
        requests: 0
      });
    }
  };

  // Helper function to check if subscription is through PayPal
  const isPayPalSubscription = () => {
    return subscription && subscription.paypal_subscription_id;
  };

  const fetchBillingHistory = async () => {
    try {
      setLoadingBilling(true);
      const response = await apiCall(
        `/api/subscriptions/billing-history?user_id=${user.uid}&app_id=${appId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch billing history");
      }

      const data = await response.json();
      setBillingHistory(data.invoices || []);
    } catch (err) {
      console.error("Error fetching billing history:", err);
      // Don't set error state, just continue with empty history
    } finally {
      setLoadingBilling(false);
    }
  };

  // Expose this function to be called by other components after resource consumption
  const refreshUsageStats = () => {
    fetchUsageStats();
  };

  const refreshSubscriptionStatus = async () => {
    if (!subscription || !subscription.id) return;

    try {
      setLoading(true);

      const response = await apiCall(
        `/api/subscriptions/sync-status/${subscription.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: user.uid,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to refresh subscription status");
      }

      // Refetch the subscription data to get the updated status
      await fetchSubscriptionData();
      
      // After refresh, check if activation completed
      if (subscription && subscription.status === 'active') {
        setHasRecentRazorpaySubscription(false);
        setSuccessMessage("Your subscription is now active!");
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage("");
        }, 5000);
      }
    } catch (err) {
      console.error("Error refreshing subscription status:", err);
      setError("Failed to refresh subscription status");
    } finally {
      setLoading(false);
    }
  };

  // Update the handleCancelSubscription function to be more robust
  const handleCancelSubscription = async () => {
    if (!subscription || !subscription.id) return;

    try {
      setCancellingSubscription(true);

      // Call the API to cancel the subscription at the end of the billing cycle
      const result = await cancelSubscription(user.uid, subscription.id);

      // Close the confirmation modal
      setShowCancelConfirm(false);

      // Update the subscription with cancellation info without changing the UI status
      setSubscription((prev) => {
        const metadata = prev.metadata || {};
        return {
          ...prev,
          metadata: {
            ...metadata,
            cancellation_scheduled: true,
          },
          // Also add at the top level for easier access
          cancellation_scheduled: true,
        };
      });

      // Show success message
      setError("");
      setSuccessMessage(
        "Your subscription has been scheduled to cancel at the end of the current billing period. You will continue to have access to all features until then."
      );

      // Clear success message after 8 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 8000);

      // Refetch subscription data for good measure
      await fetchSubscriptionData();
    } catch (err) {
      console.error("Error cancelling subscription:", err);
      setError(
        "Failed to cancel subscription. Please try again later or contact support if the issue persists."
      );
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Handle successful upgrade/subscription changes from modal
  const handleSubscriptionSuccess = async (message = "Subscription updated successfully!") => {
    // Simplified success message with FAQ link
    let basicMessage = "Subscription upgraded successfully!";
    
    if (message.includes("discount")) {
      basicMessage = "Subscription upgraded successfully with discount applied!";
    } else if (message.includes("refund")) {
      basicMessage = "Subscription upgraded successfully! A refund will be processed within 3-5 working days.";
    } else if (message.includes("temporary resources")) {
      basicMessage = "Subscription upgraded successfully! Temporary resources have been added.";
    }
    
    setSuccessMessage(
      <div className="flex items-start">
        <span>{basicMessage}</span>
        <button 
          onClick={() => setShowFAQs(true)}
          className="ml-2 text-green-300 hover:text-green-200 underline flex items-center"
        >
          <span>Learn more</span>
          <HelpCircle className="h-3 w-3 ml-1" />
        </button>
      </div>
    );
    
    await fetchSubscriptionData();
    await fetchUsageStats();
    
    // Clear success message after 12 seconds
    setTimeout(() => {
      setSuccessMessage("");
    }, 12000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isPremium = () => {
    return subscription && subscription.amount && subscription.amount > 0;
  };

  const isActive = () => {
    return subscription?.status === "active";
  };

  // Update the isCancellationScheduled function to handle different metadata structures
  const isCancellationScheduled = () => {
    if (!subscription) return false;

    return (
      (subscription.metadata && subscription.metadata.cancellation_scheduled === true) ||
      (subscription.metadata && subscription.metadata.paypal_cancelled === true) ||  // ADD THIS LINE
      subscription.cancellation_scheduled === true ||
      subscription.paypal_cancelled === true  // ADD THIS LINE TOO
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader className="h-8 w-8 text-[#ff7e00] animate-spin mb-4" />
          <p className="text-gray-300">Loading subscription data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0c1526] to-[#142039] text-white">
      <div className="container mx-auto max-w-5xl px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">
            Subscription Dashboard
          </h1>
          {/* Add FAQ button */}
          <button
            onClick={() => setShowFAQs(true)}
            className="py-2 px-4 bg-[#1e293b] hover:bg-[#273349] text-white rounded-lg flex items-center text-sm font-medium transition-colors"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Subscription FAQs
          </button>
        </div>

        {error && (
          <div className="bg-red-900 text-red-100 rounded-md p-4 mb-6 flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-300 mr-3 mt-0.5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-900 text-green-100 rounded-md p-4 mb-6 flex items-start">
            <CheckCircle className="h-5 w-5 text-green-300 mr-3 mt-0.5 flex-shrink-0" />
            <p>{successMessage}</p>
          </div>
        )}

        <div className="bg-[#1e293b] rounded-xl shadow-xl border border-gray-700 overflow-hidden mb-8">
  <div className="p-6 border-b border-gray-700">
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <Crown className="h-5 w-5 mr-2 text-[#ff7e00]" />
        <div>
          <h2 className="text-xl font-bold text-white">
            {subscription?.plan_name || "Free Plan"}
          </h2>
          {subscription && (
            <>
              <p className="text-sm text-gray-400">
                {subscription.status === "active" && "Active Subscription"}
                {subscription.status === "created" && "Subscription Pending Activation"}
                {subscription.status === "authenticated" && "Subscription Pending Activation"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Subscription changes take a few mins to reflect
              </p>
            </>
          )}
        </div>
      </div>
      <button
        onClick={() => {
          if (isCancellationScheduled()) {
            setError("Your subscription is scheduled for cancellation. Please contact support for immediate assistance if you want to resubscribe or wait until your current subscription expires.");
            return;
          }
          setShowUpgradeModal(true);
        }}
        className="py-2 px-4 bg-gradient-to-r from-[#ff7e00] to-[#e11d48] text-white rounded-lg hover:shadow-lg text-sm font-medium transition-shadow"
      >
        {isPremium() ? "Change Plan" : "Upgrade"}
      </button>
    </div>
  

            {/* Cancellation notice banner */}
            {isCancellationScheduled() && isActive() && (
              <div className="mt-4 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-medium mb-1">
                      Your subscription will end soon
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Your {subscription.plan_name} plan will remain active
                      until {formatDate(subscription.current_period_end)}. After
                      this date, you will be downgraded to the free plan. Please contact support for immediate assistance if you want to resubscribe.
                    </p>
                    
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#0c1526] rounded-lg p-4 border border-gray-700">
                <div className="flex items-center text-gray-400 text-sm mb-2">
                  <CreditCard className="h-4 w-4 mr-2" />
                  <span>Billing</span>
                </div>
                <div className="text-white font-medium">
                  {isPremium()
                    ? formatSubscriptionPrice(
                        subscription.amount,
                        subscription.currency,
                        subscription.interval
                      )
                    : "Free"}
                </div>
              </div>

              <div className="bg-[#0c1526] rounded-lg p-4 border border-gray-700">
                <div className="flex items-center text-gray-400 text-sm mb-2">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>
                    {isCancellationScheduled()
                      ? "Access Until"
                      : "Renewal Date"}
                  </span>
                </div>
                <div className="text-white font-medium">
                  {subscription?.current_period_end
                    ? formatDate(subscription.current_period_end)
                    : "N/A"}
                </div>
              </div>

              <div className="bg-[#0c1526] rounded-lg p-4 border border-gray-700">
                <div className="flex items-center text-gray-400 text-sm mb-2">
                  <Users className="h-4 w-4 mr-2" />
                  <span>Status</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-white font-medium">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        subscription?.status === "active"
                          ? "bg-green-900 text-green-300"
                          : subscription?.status === "halted" ||
                           subscription?.status === "cancelled"
                         ? "bg-red-900 text-red-300"
                         : "bg-yellow-900 text-yellow-300"
                     }`}
                   >
                     {subscription?.status || "Free"}
                   </span>
                 </div>
               </div>
             </div>
           </div>

           {/* Unified cancel subscription section for both PayPal and Razorpay */}
           {isPremium() &&
             isActive() &&
             !isCancellationScheduled() && (
               <div className="mt-6 border-t border-gray-700 pt-6">
                 <div className="flex flex-col md:flex-row md:items-center justify-between">
                   <div>
                     <h3 className="text-md font-medium text-white mb-1">
                       Cancel Subscription
                     </h3>
                     <p className="text-gray-400 text-sm">
                       Your subscription will remain active until the end of
                       the current billing period (
                       {subscription?.current_period_end
                         ? formatDate(subscription.current_period_end)
                         : "current period"}
                       ).
                     </p>
                   </div>
                   <button
                     onClick={() => setShowCancelConfirm(true)}
                     className="mt-4 md:mt-0 py-2 px-4 bg-red-900 text-red-100 rounded-lg hover:bg-red-800 text-sm font-medium flex items-center justify-center"
                   >
                     <XCircle className="h-4 w-4 mr-2" />
                     Cancel Subscription
                   </button>
                 </div>
               </div>
             )}
         </div>

         {/* Resource Usage */}
         <div className="p-6">
           <h3 className="text-lg font-medium text-white mb-4 flex items-center">
             <BarChart className="h-5 w-5 mr-2 text-[#ff7e00]" />
             Resource Usage
           </h3>

           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div>
               <UsageProgressBar
                 subscription={subscription}
                 actionType="requests"
                 currentUsage={usageStats.requests || 0}
                 className="mb-4"
                 label="Reports"
               />
             </div>
           </div>
         </div>
       </div>

       {/* Billing History */}
       {isPremium() && (
         <div className="bg-[#1e293b] rounded-xl shadow-xl border border-gray-700 overflow-hidden">
           <div className="p-6 border-b border-gray-700">
             <h2 className="text-xl font-bold text-white">Billing History</h2>
           </div>

           <div className="p-6">
             {loadingBilling ? (
               <div className="flex justify-center py-8">
                 <Loader className="h-8 w-8 text-[#ff7e00] animate-spin" />
               </div>
             ) : billingHistory.length > 0 ? (
               <div className="overflow-x-auto">
                 <table className="min-w-full divide-y divide-gray-700">
                   <thead className="bg-[#0c1526]">
                     <tr>
                       <th
                         scope="col"
                         className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                       >
                         Date
                       </th>
                       <th
                         scope="col"
                         className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                       >
                         Amount
                       </th>
                       <th
                         scope="col"
                         className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                       >
                         Status
                       </th>
                     </tr>
                   </thead>
                   <tbody className="bg-[#1e293b] divide-y divide-gray-700">
                     {billingHistory.map((invoice) => (
                       <tr key={invoice.id}>
                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                           {formatDate(invoice.invoice_date)}
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                           {formatSubscriptionPrice(
                             invoice.amount,
                             invoice.currency
                           )}
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm">
                           <span
                             className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                               invoice.status === "Paid"
                                 ? "bg-green-900 text-green-300"
                                 : invoice.status === "failed"
                                 ? "bg-red-900 text-red-300"
                                 : "bg-yellow-900 text-yellow-300"
                             }`}
                           >
                             {invoice.status}
                           </span>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
             ) : (
               <div className="text-center py-8 text-gray-400">
                 <p>No billing history available</p>
               </div>
             )}
           </div>
         </div>
       )}
     </div>

     {/* Subscription Modal */}
     <SubscriptionModal
       isOpen={showUpgradeModal}
       onClose={() => setShowUpgradeModal(false)}
       user={user}
       currentPlan={subscription}
       appId={appId}
       onSuccess={handleSubscriptionSuccess}
     />

     {/* FAQ Modal */}
     <SubscriptionFAQs
       isOpen={showFAQs}
       onClose={() => setShowFAQs(false)}
     />

     {/* Cancel Confirmation Modal */}
     {showCancelConfirm && (
       <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
         <div className="bg-[#1e293b] rounded-xl shadow-xl border border-gray-700 max-w-md w-full p-6 relative">
           <h3 className="text-xl font-bold text-white mb-4">
             Cancel Subscription
           </h3>

           <p className="text-gray-300 mb-4">
             Are you sure you want to cancel your subscription? Your plan will
             remain active until
             <span className="font-medium text-white">
               {" "}
               {formatDate(subscription.current_period_end)}
             </span>
             , after which you will be downgraded to the free plan.
           </p>

           {subscription?.features &&
             Object.entries(subscription.features).length > 0 && (
               <div className="mb-4 p-3 bg-[#0c1526] rounded-lg border border-gray-700">
                 <p className="text-sm text-yellow-300 mb-2 flex items-center">
                   <AlertTriangle className="h-4 w-4 mr-1" /> After{" "}
                   {formatDate(subscription.current_period_end)}, you will lose
                   access to:
                 </p>
                 <ul className="text-sm text-gray-300 space-y-1 ml-6 list-disc">
                   {Object.entries(subscription.features).map(
                     ([key, value]) => (
                       <li key={key}>
                         {typeof value === "boolean" ? (
                           <span className="capitalize">{key}</span>
                         ) : (
                           <span>
                             <span className="font-medium text-white">
                               {value}
                             </span>{" "}
                             {key}
                           </span>
                         )}
                       </li>
                     )
                   )}
                 </ul>
               </div>
             )}

           <div className="flex justify-end space-x-3">
             <button
               onClick={() => setShowCancelConfirm(false)}
               className="py-2 px-4 bg-[#273349] text-white rounded-lg hover:bg-[#324056] text-sm font-medium"
               disabled={cancellingSubscription}
             >
               Keep Subscription
             </button>
             <button
               onClick={handleCancelSubscription}
               className="py-2 px-4 bg-red-900 text-red-100 rounded-lg hover:bg-red-800 text-sm font-medium flex items-center justify-center"
               disabled={cancellingSubscription}
             >
               {cancellingSubscription ? (
                 <>
                   <Loader className="h-4 w-4 mr-2 animate-spin" />
                   Processing...
                 </>
               ) : (
                 <>
                   <XCircle className="h-4 w-4 mr-2" />
                   Cancel Subscription
                 </>
               )}
             </button>
           </div>
         </div>
       </div>
     )}
   </div>
 );
};

export default SubscriptionDashboard;